import { c as _c } from "react/compiler-runtime";
import { type Dispatch, type RefObject, type SetStateAction, useEffect, useRef, useState } from 'react';
const scrollBoundaryTolerance = 1;

// eslint-disable-next-line @typescript-eslint/naming-convention -- Exported constants use CONSTANT_CASE.
export const HORIZONTAL_SCROLL_CONTROL_ATTRIBUTE = 'data-horizontal-scroll-control';

/** The raw viewport numbers needed to decide which edges can still scroll. */
export type HorizontalOverflowMeasurements = {
  clientWidth: number;
  scrollLeft: number;
  scrollWidth: number;
};
export type HorizontalOverflowState = {
  canScrollEnd: boolean;
  canScrollStart: boolean;
};
export type HorizontalScrollDirection = 'end' | 'start';
export type HorizontalScrollDeltaOptions = {
  clientWidth: number;
  direction: HorizontalScrollDirection;
  isRtl: boolean;
};

/** The raw viewport numbers needed to work out the scrollbar's thickness. */
export type HorizontalScrollbarMeasurements = {
  borderBottomWidth: number;
  borderTopWidth: number;
  clientHeight: number;
  offsetHeight: number;
};
const noHorizontalOverflow: HorizontalOverflowState = {
  canScrollEnd: false,
  canScrollStart: false
};
type HorizontalScrollAreaMeasurementsState = HorizontalOverflowState & {
  scrollbarHeight: number;
};
const initialMeasurementsState: HorizontalScrollAreaMeasurementsState = {
  ...noHorizontalOverflow,
  scrollbarHeight: 0
};

/** Finds the scroll viewport among the wrapper's arrow-control siblings. */
export const getHorizontalScrollViewport = (wrapper: HTMLElement): HTMLElement | undefined => {
  const scrollViewport = [...wrapper.children].find(child => !child.matches(`[${HORIZONTAL_SCROLL_CONTROL_ATTRIBUTE}]`));
  return scrollViewport instanceof HTMLElement ? scrollViewport : undefined;
};

/**
 * Determines which edges still have hidden content to scroll to.
 */
export const getHorizontalOverflowState = ({
  clientWidth,
  scrollLeft,
  scrollWidth
}: HorizontalOverflowMeasurements): HorizontalOverflowState => {
  const maximumScrollOffset = Math.max(0, scrollWidth - clientWidth);
  if (maximumScrollOffset <= scrollBoundaryTolerance) {
    return noHorizontalOverflow;
  }
  const scrollOffset = Math.min(Math.abs(scrollLeft), maximumScrollOffset);
  const isAtStart = scrollOffset <= scrollBoundaryTolerance;
  const isAtEnd = scrollOffset >= maximumScrollOffset - scrollBoundaryTolerance;
  return {
    canScrollEnd: !isAtEnd,
    canScrollStart: !isAtStart
  };
};

/** Returns a one-viewport scroll step in the requested logical direction. */
export const getHorizontalScrollDelta = ({
  clientWidth,
  direction,
  isRtl
}: HorizontalScrollDeltaOptions): number => {
  const physicalDirection = isRtl ? -1 : 1;
  return clientWidth * physicalDirection * (direction === 'end' ? 1 : -1);
};

/**
 * Get height of the scrollbar so indicators don't stack on top.
 * `0` for overlay or hidden scrollbars.
 */
export const getHorizontalScrollbarHeight = ({
  borderBottomWidth,
  borderTopWidth,
  clientHeight,
  offsetHeight
}: HorizontalScrollbarMeasurements): number => Math.max(0, offsetHeight - clientHeight - borderTopWidth - borderBottomWidth);
export type HorizontalScrollAreaState = HorizontalOverflowState & {
  scrollEnd: () => void;
  scrollbarHeight: number;
  scrollStart: () => void;
  wrapperRef: RefObject<HTMLDivElement | null>;
};

/**
 * Wires scroll/resize/mutation observers to a single scroll viewport and
 * reports edge/scrollbar measurements through `setMeasurementsState`. Returns a
 * teardown, or `undefined` when the environment lacks `ResizeObserver` (older
 * browsers keep the original scrolling behavior without indicators).
 */
const observeScrollViewport = (scrollViewport: HTMLElement, setMeasurementsState: Dispatch<SetStateAction<HorizontalScrollAreaMeasurementsState | undefined>>): (() => void) | undefined => {
  // The child is expected to be the scroll viewport itself. If it can't
  // scroll horizontally the indicators silently never appear, so surface
  // the most common misuse (wrong element, or overflow-x left visible).
  if (process.env.NODE_ENV !== 'production' && getComputedStyle(scrollViewport).overflowX === 'visible') {
    console.warn('HorizontalScrollArea: the direct child is not horizontally scrollable ' + '(overflow-x is "visible"), so overflow indicators will not appear. ' + 'Ensure the child is the scroll viewport with overflow-x: auto or scroll.');
  }

  // Older browsers retain the original scroll behavior without indicators.
  if (globalThis.ResizeObserver === undefined) {
    return undefined;
  }

  // Layout values change only on resize/content mutations, not while scrolling.
  // Reading them forces a layout, so we cache them and keep the scroll handler
  // limited to reading scrollLeft.
  let clientWidth = 0;
  let scrollWidth = 0;
  let scrollbarHeight = 0;
  let scrollFrame: number | undefined;
  let layoutFrame: number | undefined;
  const applyMeasurements = (): void => {
    const nextOverflowState = getHorizontalOverflowState({
      clientWidth,
      scrollLeft: scrollViewport.scrollLeft,
      scrollWidth
    });
    setMeasurementsState(currentState => {
      if (currentState !== undefined && currentState.canScrollEnd === nextOverflowState.canScrollEnd && currentState.canScrollStart === nextOverflowState.canScrollStart && currentState.scrollbarHeight === scrollbarHeight) {
        return currentState;
      }
      return {
        ...nextOverflowState,
        scrollbarHeight
      };
    });
  };
  const updateScrollPosition = (): void => {
    scrollFrame = undefined;
    applyMeasurements();
  };
  const scheduleScrollUpdate = (): void => {
    if (scrollFrame !== undefined) {
      return;
    }
    scrollFrame = requestAnimationFrame(updateScrollPosition);
  };
  const measureLayout = (): void => {
    layoutFrame = undefined;
    const computedStyle = getComputedStyle(scrollViewport);
    clientWidth = scrollViewport.clientWidth;
    scrollWidth = scrollViewport.scrollWidth;

    // Need to get scrollbar height to make sure UI indicators don't overlap.
    scrollbarHeight = getHorizontalScrollbarHeight({
      borderBottomWidth: Number.parseFloat(computedStyle.borderBottomWidth) || 0,
      borderTopWidth: Number.parseFloat(computedStyle.borderTopWidth) || 0,
      clientHeight: scrollViewport.clientHeight,
      offsetHeight: scrollViewport.offsetHeight
    });
    applyMeasurements();
  };
  const scheduleLayoutUpdate = (): void => {
    if (layoutFrame !== undefined) {
      return;
    }
    layoutFrame = requestAnimationFrame(measureLayout);
  };
  const resizeObserver = new ResizeObserver(scheduleLayoutUpdate);
  const observeViewportAndContent = (): void => {
    resizeObserver.disconnect();
    resizeObserver.observe(scrollViewport);
    for (const child of scrollViewport.children) {
      resizeObserver.observe(child);
    }
  };
  const mutationObserver = new MutationObserver(() => {
    observeViewportAndContent();
    scheduleLayoutUpdate();
  });
  scrollViewport.addEventListener('scroll', scheduleScrollUpdate, {
    passive: true
  });
  mutationObserver.observe(scrollViewport, {
    childList: true
  });
  observeViewportAndContent();
  scheduleLayoutUpdate();
  return () => {
    scrollViewport.removeEventListener('scroll', scheduleScrollUpdate);
    mutationObserver.disconnect();
    resizeObserver.disconnect();
    if (scrollFrame !== undefined) {
      cancelAnimationFrame(scrollFrame);
    }
    if (layoutFrame !== undefined) {
      cancelAnimationFrame(layoutFrame);
    }
  };
};

/**
 * Watches the wrapper's scroll viewport child and reports which edges can
 * scroll (for the fade indicators) plus the scrollbar height.
 */
export const useHorizontalScrollArea = () => {
  const $ = _c(7);
  const wrapperRef = useRef(null);
  const [measurementsState, setMeasurementsState] = useState();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        return;
      }
      let teardownViewport;
      let resetFrame;
      const cancelMeasurementReset = () => {
        if (resetFrame === undefined) {
          return;
        }
        cancelAnimationFrame(resetFrame);
        resetFrame = undefined;
      };
      const scheduleMeasurementReset = () => {
        if (resetFrame !== undefined) {
          return;
        }
        resetFrame = requestAnimationFrame(() => {
          resetFrame = undefined;
          setMeasurementsState(undefined);
        });
      };
      const attachToChild = () => {
        cancelMeasurementReset();
        teardownViewport?.();
        teardownViewport = undefined;
        const scrollViewport = getHorizontalScrollViewport(wrapper);
        if (!scrollViewport) {
          scheduleMeasurementReset();
          return;
        }
        teardownViewport = observeScrollViewport(scrollViewport, setMeasurementsState);
      };
      const childObserver = new MutationObserver(attachToChild);
      childObserver.observe(wrapper, {
        childList: true
      });
      attachToChild();
      return () => {
        childObserver.disconnect();
        teardownViewport?.();
        cancelMeasurementReset();
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = direction => {
      const scrollViewport_0 = wrapperRef.current ? getHorizontalScrollViewport(wrapperRef.current) : undefined;
      if (!scrollViewport_0) {
        return;
      }
      scrollViewport_0.scrollBy({
        left: getHorizontalScrollDelta({
          clientWidth: scrollViewport_0.clientWidth,
          direction,
          isRtl: getComputedStyle(scrollViewport_0).direction === "rtl"
        })
      });
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const scroll = t2;
  const t3 = measurementsState ?? initialMeasurementsState;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => scroll("end");
    t5 = () => scroll("start");
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t3) {
    t6 = {
      ...t3,
      scrollEnd: t4,
      scrollStart: t5,
      wrapperRef
    };
    $[5] = t3;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
};